import axios, { type AxiosInstance } from 'axios';
import { bridgeAuthorizationForRequest } from '../commerce/ensureBridgeToken';

const COMMERCE_GATEWAY_PROXY_BASE_URL = '/api/commerce-gateway';
const BRIDGE_AUTHORIZATION_HEADER = 'X-Authorization';
/** The gateway authenticates the caller from this before reading the bridge token. */
const USER_AUTHORIZATION_HEADER = 'Authorization';
const BRIDGE_ROUTE_PREFIX = '/commerce/bridge/';

/** Non-prod (staging/ephemeral/dev/test) all target staging USG. */
export const resolveUsgOrigin = (): string =>
  // Literal access required: Next.js only inlines NEXT_PUBLIC_* into the
  // client bundle for literal `process.env.NEXT_PUBLIC_*` reads.
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
    ? 'https://services.unity.com'
    : 'https://staging.services.unity.com';

export type CommerceClientTarget = 'usg-direct' | 'proxy';

/**
 * Default stays 'proxy': surfaces opt into usg-direct one at a time as their
 * bridge-token provisioning is wired up (PDP Buy Now first, CPS-7658), and
 * repointing an opted-in surface back at the proxy is the Phase 0 rollback
 * (TDD §9).
 */
const DEFAULT_COMMERCE_CLIENT_TARGET: CommerceClientTarget = 'proxy';

const attachBridgeAuthorization = (client: AxiosInstance) => {
  client.interceptors.request.use(async (config) => {
    // Only bridge routes take X-Authorization; commerce-core issues
    // identity/account calls (/unity/...) through this same client, and
    // those need the proxy's Genesis Authorization + X-Client-ID.
    if (!config.url?.startsWith(BRIDGE_ROUTE_PREFIX)) {
      config.baseURL = COMMERCE_GATEWAY_PROXY_BASE_URL;
      return config;
    }
    // Resolved per request, so a token minted, exchanged or renewed
    // mid-session is picked up: no mount gate ran for this one — a React Query
    // refetch on window focus can fire under a page left mounted past the
    // access token's lifetime — so it also enforces the token's subject and
    // renews from the refresh cookie. No usable token sends no header at all.
    const auth = await bridgeAuthorizationForRequest(resolveUsgOrigin());
    if (auth) {
      config.headers.set(BRIDGE_AUTHORIZATION_HEADER, auth.xAuthorization);
      if (auth.authorization) {
        config.headers.set(USER_AUTHORIZATION_HEADER, auth.authorization);
      }
    }
    return config;
  });
  client.interceptors.response.use(undefined, (error) => {
    // The axios rejection carries the request config; strip both bearers
    // before they reach logs/Sentry (same property bridgeToken.ts preserves
    // via BridgeTokenRequestError). `Authorization` carries the Unity ID JWT
    // and its user claims, so it matters at least as much as the bridge token.
    if (axios.isAxiosError(error)) {
      error.config?.headers?.delete(BRIDGE_AUTHORIZATION_HEADER);
      error.config?.headers?.delete(USER_AUTHORIZATION_HEADER);
    }
    return Promise.reject(error);
  });
};

/**
 * Browser-direct USG client: gateway rate limits key on the user's IP instead
 * of our few shared egress IPs, and guest carts become possible — the
 * /api/commerce-gateway proxy 401s logged-out users. Even in usg-direct mode
 * only /commerce/bridge/ routes go direct; everything else stays on the proxy.
 */
export const createCommerceAxiosClient = (
  target: CommerceClientTarget = DEFAULT_COMMERCE_CLIENT_TARGET
): AxiosInstance => {
  const client = axios.create({
    baseURL:
      target === 'proxy'
        ? COMMERCE_GATEWAY_PROXY_BASE_URL
        : `${resolveUsgOrigin()}/api`,
    headers: { 'Content-Type': 'application/json' },
  });
  if (target === 'usg-direct') {
    attachBridgeAuthorization(client);
  }
  return client;
};
